import type { Shipment } from 'common/src/utils/types';
import type { ShipmentHeaderUpdate } from 'common/src/api/shipments';

export type ShipmentDetailsTab =
  | 'general'
  | 'packing-list'
  | 'tracking'
  | 'prices'
  | 'logbook'
  | 'cases';
export type ShipmentHeaderForm = Required<
  Record<keyof ShipmentHeaderUpdate, string>
>;

export const shipmentHeaderFields: Array<{
  key: keyof ShipmentHeaderUpdate;
  label: string;
  type?: 'date';
}> = [
  { key: 'reference_number', label: 'Referencia' },
  { key: 'receiver', label: 'Cliente' },
  { key: 'container_number', label: 'Contenedor' },
  { key: 'packing_name', label: 'Packing' },
  { key: 'packing_etd', label: 'Fecha Despacho Packing', type: 'date' },
  { key: 'ship_name', label: 'Nave' },
  { key: 'ship_type', label: 'Tipo Nave' },
  { key: 'ship_etd', label: 'ETD', type: 'date' },
  { key: 'ship_eta', label: 'ETA', type: 'date' },
  { key: 'ship_origin_port', label: 'Puerto Origen' },
  { key: 'ship_origin_country', label: 'País Origen' },
  { key: 'ship_destination_port', label: 'Puerto Destino' },
  { key: 'ship_destination_country', label: 'País Destino' },
  { key: 'business_type', label: 'Tipo de Negocio' },
  { key: 'sale_order', label: 'Orden de Venta' },
  { key: 'original_incoterm', label: 'Incoterm' },
  { key: 'invoice_number', label: 'Factura SII' },
  { key: 'sale_region', label: 'Región de Venta' },
  { key: 'sale_market', label: 'Mercado de Venta' },
  { key: 'freight_type', label: 'Tipo Flete' },
  { key: 'booking_number', label: 'Booking' },
  { key: 'awb_number', label: 'AWB' },
];

export const emptyShipmentHeaderForm = shipmentHeaderFields.reduce(
  (acc, field) => {
    acc[field.key] = '';
    return acc;
  },
  {} as ShipmentHeaderForm
);

const toDateInputValue = (value?: string | null) => {
  if (!value) return '';
  return value.slice(0, 10);
};

export const buildShipmentHeaderForm = (
  shipment?: ShipmentHeaderUpdate | null
): ShipmentHeaderForm =>
  shipmentHeaderFields.reduce((acc, field) => {
    const value = shipment?.[field.key];
    acc[field.key] =
      field.type === 'date'
        ? toDateInputValue(value as string | null | undefined)
        : String(value ?? '');
    return acc;
  }, {} as ShipmentHeaderForm);

const trackingIdentityFields: Array<{
  key: 'container_number' | 'awb_number' | 'ship_etd' | 'ship_eta';
  label: string;
}> = [
  { key: 'container_number', label: 'Contenedor' },
  { key: 'awb_number', label: 'AWB' },
  { key: 'ship_etd', label: 'ETD' },
  { key: 'ship_eta', label: 'ETA' },
];

const normalizeTrackingIdentityValue = (
  key: (typeof trackingIdentityFields)[number]['key'],
  value?: string | null
) => {
  if (key === 'container_number') {
    return (value || '')
      .trim()
      .toUpperCase()
      .replace(/[\s-]+/g, '');
  }
  if (key === 'awb_number') {
    return (value || '').trim().replace(/[\s-]+/g, '');
  }
  return toDateInputValue(value);
};

export const getChangedTrackingIdentityFields = (
  shipment: Shipment,
  form: ShipmentHeaderForm
) =>
  trackingIdentityFields.filter(
    ({ key }) =>
      normalizeTrackingIdentityValue(key, shipment[key] as string | null) !==
      normalizeTrackingIdentityValue(key, form[key])
  );
